<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import LabelItem from 'dashboard/components-next/label/LabelItem.vue';
import AddLabel from 'dashboard/components-next/label/AddLabel.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const hoveredLabel = ref(null);

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  return allLabels.value.filter(({ title }) =>
    availableContactLabels.includes(title)
  );
});

const labelMenuItems = computed(() => {
  return allLabels.value
    ?.map(label => ({
      label: label.title,
      value: label.id,
      pillLabel: {
        title: label.title,
        color: label.color,
        emoji: label.emoji,
      },
      thumbnail: {
        name: label.title,
        color: label.color,
        emoji: label.emoji,
      },
      isSelected: savedLabels.value.some(
        savedLabel => savedLabel.id === label.id
      ),
      action: 'contactLabel',
    }))
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected));
});

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }
  store.dispatch('contactLabels/get', contactId);
};

const handleLabelAction = async ({ value }) => {
  try {
    const currentLabels = savedLabels.value.map(label => label.title);

    const selectedLabel = allLabels.value.find(label => label.id === value);
    if (!selectedLabel) return;

    let updatedLabels;

    if (currentLabels.includes(selectedLabel.title)) {
      updatedLabels = currentLabels.filter(
        labelTitle => labelTitle !== selectedLabel.title
      );
    } else {
      updatedLabels = [...currentLabels, selectedLabel.title];
    }

    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });
  } catch (error) {
    // error
  }
};

const handleRemoveLabel = label => {
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchLabels(newVal);
    }
  }
);
onMounted(() => {
  if (route.params.contactId) {
    fetchLabels(route.params.contactId);
  }
});

const handleMouseLeave = () => {
  hoveredLabel.value = null;
};

const handleLabelHover = labelId => {
  hoveredLabel.value = labelId;
};
</script>

<template>
  <div class="min-w-0 space-y-2" data-contact-labels-root>
    <div class="flex min-w-0 items-center justify-between gap-2">
      <h3 class="mb-0 min-w-0 truncate text-sm font-bold text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.TABLE.LABELS') }}
      </h3>
      <AddLabel
        icon-only
        contained
        class="shrink-0"
        :label-menu-items="labelMenuItems"
        @update-label="handleLabelAction"
      />
    </div>
    <div
      class="flex flex-wrap items-center gap-2"
      @mouseleave="handleMouseLeave"
    >
      <LabelItem
        v-for="label in savedLabels"
        :key="label.id"
        :label="label"
        :is-hovered="hoveredLabel === label.id"
        @remove="handleRemoveLabel"
        @hover="handleLabelHover(label.id)"
      />
    </div>
  </div>
</template>
